import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, X } from "lucide-react";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { supabase } from "@/integrations/supabase/client";

const COOKIE = "bp_diag_banner_dismissed";
const DELAY_MS = 30_000;

function hasDismissCookie(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie.split("; ").some((c) => c.startsWith(`${COOKIE}=1`));
}

function setDismissCookie() {
  const d = new Date();
  d.setTime(d.getTime() + 30 * 24 * 60 * 60 * 1000);
  document.cookie = `${COOKIE}=1; expires=${d.toUTCString()}; path=/; SameSite=Lax`;
}

export function DiagnosticBanner() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [authed, setAuthed] = useState<boolean | null>(null);

  // Skip on the diagnostic route itself
  const onDiagnostic = /\/diagnostic(\/|$)/.test(pathname);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setAuthed(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (mounted) setAuthed(!!session);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (onDiagnostic) return;
    if (authed !== false) return;
    if (hasDismissCookie()) return;
    const id = window.setTimeout(() => setVisible(true), DELAY_MS);
    return () => window.clearTimeout(id);
  }, [authed, onDiagnostic]);

  if (!visible || onDiagnostic || authed !== false) return null;

  const dismiss = () => {
    setDismissCookie();
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-label={t("marketing.diagBanner.aria", { defaultValue: "Diagnostic suggestion" })}
      className="fixed inset-x-0 bottom-0 z-40 h-10 bg-gradient-to-r from-primary via-primary to-primary/85 text-primary-foreground shadow-md md:bottom-auto md:top-0 animate-in fade-in slide-in-from-top-2 duration-500"
    >
      <div className="container flex h-10 items-center justify-between gap-3 text-sm font-medium">
        <Link
          to="/diagnostic"
          className="flex flex-1 items-center gap-2 truncate text-primary-foreground/95 transition-colors hover:text-primary-foreground"
        >
          <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
          <span className="truncate">
            {t("marketing.diagBanner.copy", {
              defaultValue: "Not sure where to start? Take the 90-second diagnostic →",
            })}
          </span>
        </Link>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t("common.dismiss", { defaultValue: "Dismiss" })}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-primary-foreground/80 transition-colors hover:bg-primary-foreground/15 hover:text-primary-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
